import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, MapPin, Trash2, Edit, Store, Coffee, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "planning", label: "Planning", color: "bg-amber-100 text-amber-700" },
  { value: "build_out", label: "Build Out", color: "bg-blue-100 text-blue-700" },
  { value: "open", label: "Open", color: "bg-emerald-100 text-emerald-700" },
  { value: "closed", label: "Closed", color: "bg-stone-200 text-stone-600" },
];

const emptyForm = { name: "", city: "", state: "", address: "", status: "planning", notes: "", target_open_date: "" };

export default function StoreManager() {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${API}/stores`);
      setStores(res.data);
    } catch (e) {
      toast.error("Failed to load stores");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Store name is required");
      return;
    }
    try {
      const payload = { ...form, target_open_date: form.target_open_date || null };
      if (editingStore) {
        await axios.put(`${API}/stores/${editingStore.id}`, payload);
        toast.success("Store updated");
      } else {
        await axios.post(`${API}/stores`, payload);
        toast.success("Store added");
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingStore(null);
      fetchStores();
    } catch (e) {
      toast.error("Failed to save store");
    }
  };

  const deleteStore = async (id) => {
    try {
      await axios.delete(`${API}/stores/${id}`);
      toast.success("Store deleted");
      fetchStores();
    } catch (e) {
      toast.error("Failed to delete store");
    }
  };

  const openEdit = (store) => {
    setEditingStore(store);
    setForm({
      name: store.name,
      city: store.city,
      state: store.state,
      address: store.address,
      status: store.status,
      notes: store.notes || "",
      target_open_date: store.target_open_date || "",
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingStore(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === status);
    return opt ? (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>
        {opt.label}
      </span>
    ) : null;
  };

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="store-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <Store className="w-5 h-5 text-stone-50" />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-stone-500">
              Location Manager
            </span>
          </div>
          <h1
            data-testid="stores-title"
            className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-semibold text-stone-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Your Stores
          </h1>
          <p className="text-sm sm:text-base text-stone-500 mt-2">
            Manage your coffee shop locations and track their progress.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-store-dialog-btn"
              onClick={openNew}
              className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all duration-200 font-medium px-6 py-2.5 btn-scale w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Store
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
                {editingStore ? "Edit Store" : "Add New Store"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-stone-700">Store Name *</Label>
                <Input
                  id="name"
                  data-testid="store-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Downtown Flagship"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="city" className="text-sm font-medium text-stone-700">City</Label>
                  <Input
                    id="city"
                    data-testid="store-city-input"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Austin"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="state" className="text-sm font-medium text-stone-700">State</Label>
                  <Input
                    id="state"
                    data-testid="store-state-input"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    placeholder="TX"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address" className="text-sm font-medium text-stone-700">Address</Label>
                <Input
                  id="address"
                  data-testid="store-address-input"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="123 Main St"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="store-status-select" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700">Target Opening Date</Label>
                <Input
                  data-testid="store-target-date-input"
                  type="date"
                  value={form.target_open_date}
                  onChange={(e) => setForm({ ...form, target_open_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="notes" className="text-sm font-medium text-stone-700">Notes</Label>
                <textarea
                  id="notes"
                  data-testid="store-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                  className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/5 focus:border-stone-400"
                />
              </div>
              <Button
                type="submit"
                data-testid="store-submit-btn"
                className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all duration-200 font-medium"
              >
                {editingStore ? "Update Store" : "Add Store"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Store Grid */}
      {stores.length === 0 && !loading ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Coffee className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="text-lg font-medium text-stone-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            No stores yet
          </h3>
          <p className="text-sm text-stone-500 mb-6">Add your first store location to get started.</p>
          <Button
            data-testid="first-store-btn"
            onClick={openNew}
            className="bg-stone-900 text-stone-50 hover:bg-stone-800"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Your First Store
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {stores.map((store, idx) => (
            <Card
              key={store.id}
              data-testid={`store-card-${store.id}`}
              className="card-hover bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl animate-slide-up"
              style={{ animationDelay: `${idx * 0.08}s` }}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-stone-900 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {store.name}
                    </h3>
                    {(store.city || store.state) && (
                      <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {[store.city, store.state].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                  {getStatusBadge(store.status)}
                </div>
                {store.address && (
                  <p className="text-sm text-stone-400 mb-3">{store.address}</p>
                )}
                {store.notes && (
                  <p className="text-sm text-stone-500 mb-4 line-clamp-2">{store.notes}</p>
                )}
                {store.target_open_date && (
                  <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-lg inline-flex items-center gap-1 mb-3">
                    <CalendarClock className="w-3 h-3" />
                    Opens: {new Date(store.target_open_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
                <div className="flex gap-2 mt-4 pt-4 border-t border-stone-100">
                  <Button
                    data-testid={`view-store-${store.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/stores/${store.id}`)}
                    className="text-xs border-stone-200 hover:bg-stone-50 flex-1"
                  >
                    <Store className="w-3 h-3 mr-1" /> View Store
                  </Button>
                  <Button
                    data-testid={`edit-store-${store.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(store)}
                    className="text-xs border-stone-200 hover:bg-stone-50"
                  >
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button
                    data-testid={`delete-store-${store.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => deleteStore(store.id)}
                    className="text-xs border-stone-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
